import { z } from 'zod';

import { AppMetadata } from '@/lib/auth/metadata/app-metadata';
import { parseISO8601DateTime } from '@/utils/dates';

type ListMixin = {
  start: number;
  limit: number;
  length: number;
  total: number;
  next: string;
};

export type OrganizationMemberList = {
  members: OrganizationMember[];
} & ListMixin;

export type OrganizationMember = {
  user_id: string;
  picture: string;
  name: string;
  email: string;
  roles: OrganizationMemberListRole[];
};

export type OrganizationMemberListRole = {
  id: string;
  name: string;
  description?: string;
};

// Auth0 stores arbitrary JSON in app_metadata; the portal reads and writes the
// AppMetadataSchema shape but must tolerate extra keys.
export type AppMetadataPayload = Partial<AppMetadataType> &
  Record<string, unknown>;

export type InviteUserPayload = {
  inviter: { name: string };
  invitee: { email: string };
  // Number of seconds for which the invitation is valid before expiration.
  // If unspecified or set to 0, defaults to 604800 seconds (7 days).
  // Max value: 2592000 seconds (30 days).
  ttl_sec?: number;

  // Data related to the user that does affect the application's core functionality.
  app_metadata?: AppMetadataPayload;

  // Data related to the user that does not affect the application's core functionality.
  user_metadata?: Record<string, unknown>;

  // List of roles IDs associated with the user.
  roles?: string[];

  // Whether the user will receive an invitation email (true) or no email (false), true by default.
  send_invitation_email?: boolean;

  // Auth0 client ID. Used to resolve the application's login initiation endpoint.
  client_id?: string;
};

export type OrganizationInvitation = {
  // The id of the user invitation.
  id: string;

  // Organization identifier
  organization_id: string;

  inviter: { name: string };

  invitee: { email: string };

  // The invitation URL to be sent to the invitee.
  invitation_url?: string;

  // The ISO 8601 formatted timestamp representing the creation time of the invitation.
  created_at?: string;

  // Number of seconds for which the invitation is valid before expiration.
  // If unspecified or set to 0, defaults to 604800 seconds (7 days).
  // Max value: 2592000 seconds (30 days).
  ttl_sec?: number;

  // The ISO 8601 formatted timestamp representing the expiration time of the invitation.
  expires_at?: string;

  // Auth0 client ID. Used to resolve the application's login initiation endpoint.
  client_id?: string;

  // The id of the connection to force invitee to authenticate with.
  connection_id?: string;

  // Data related to the user that does affect the application's core functionality.
  app_metadata?: AppMetadataPayload;

  // Data related to the user that does not affect the application's core functionality.
  user_metadata?: Record<string, unknown>;

  // List of roles IDs associated with the user.
  roles: Role[];

  // The id of the invitation ticket.
  ticket_id?: string;

  // Whether the user will receive an invitation email (true) or no email (false), true by default.
  send_invitation_email?: boolean;
};

export type OrganizationInvitationList = {
  invitations: OrganizationInvitation[];
} & ListMixin;

export type Role = {
  id: string;
  name: string;
  description: string;
};

export type RoleList = {
  roles: Role[];
} & ListMixin;

export const AppMetadataSchema = z.object({
  location_ids: z
    .array(z.string())
    .optional()
    .transform((arg) => arg || []),
  locations: z
    .record(
      z.string(),
      z.object({
        sublocations: z.array(z.string()),
        has_access_to_root_location: z.boolean().default(false),
      })
    )
    .optional()
    .transform((locations) => {
      if (!locations) {
        return {};
      }

      return locations;
    }),
  report_types: z
    .array(z.string()) // TODO: replace inner string with enum report type
    .optional()
    .transform((reportTypes) => {
      if (!reportTypes) {
        return [];
      }

      return reportTypes;
    }),
  has_access_to_portal: z.boolean().default(false),
  is_gov_officer: z.boolean().default(false),
  // A malformed grant must not discard the rest of the metadata.
  all_locations: z.boolean().optional().catch(undefined),
});

export type AppMetadataType = z.infer<typeof AppMetadataSchema>;

const rawAppMetadataSchema = z.record(z.string(), z.unknown()).nullish();

export type RawAppMetadataType = z.infer<typeof rawAppMetadataSchema>;

export const UserSchema = z.object({
  user_id: z.string(),
  email: z.string().email(),
  name: z.string(),
  given_name: z.string().optional(),
  family_name: z.string().optional(),
  username: z.string().optional(),
  nickname: z.string().optional(),
  last_ip: z.string().optional(),
  logins_count: z.number(),
  email_verified: z.boolean(),
  picture: z.string(),
  app_metadata: rawAppMetadataSchema.transform((val) => new AppMetadata(val)),
  user_metadata: z.record(z.string(), z.any()).optional(),
  updated_at: z.string().transform((val) => parseISO8601DateTime(val)),
  last_login: z.string().transform((val) => parseISO8601DateTime(val)),
  created_at: z.string().transform((val) => parseISO8601DateTime(val)),
});

export type User = z.infer<typeof UserSchema>;

export type UserOrgMemberPayload = {
  app_metadata: AppMetadataPayload;
  rolesToAssign: string[];
  rolesToDelete: string[];
};

export type AuthResourcesPaginationOptions = {
  page: number;
  per_page: number;
  search?: string;
};
